// Sync History — shared type surface and mock dataset.
//
// Every other component in components/sync-history/ imports from this file.
// All data here is hardcoded mock data. Timestamps are anchored to
// 2026-04-12T14:30:00Z so the dataset always looks "just happened".

// Enums ------------------------------------------------------------
export type SyncStatus = 'success' | 'running' | 'failed' | 'partial';
export type SyncSource =
  | 'confluence'
  | 'gmail'
  | 'jira'
  | 'gcal'
  | 'quickbooks';
export type RecordChangeType = 'new' | 'updated' | 'failed';

// Preview payloads (discriminated union by source) -----------------
export type PreviewPayload =
  | { source: 'confluence'; markdown: string }
  | {
      source: 'gmail';
      from: string;
      to: string;
      subject: string;
      body: string;
    }
  | {
      source: 'jira';
      fields: { label: string; value: string }[];
      description: string;
    }
  | {
      source: 'gcal';
      start: string;
      end: string;
      attendees: string[];
      location: string;
      description: string;
    }
  | {
      source: 'quickbooks';
      number: string;
      customer: string;
      lineItems: { label: string; amount: string }[];
      total: string;
      status: string;
    };

// Diff payloads ----------------------------------------------------
export type DiffPayload =
  | { kind: 'prose'; before: string; after: string }
  | {
      kind: 'fields';
      changes: { label: string; before: string; after: string }[];
    };

// Classification + summary ----------------------------------------
export interface ClassificationPayload {
  label: string;
  confidence: number;
  model: string;
  latencyMs: number;
  reasoning: string;
}

export interface SummaryPayload {
  chunkCount: number;
  embeddingModel: string;
  summary: string;
}

// Records and runs -------------------------------------------------
export interface SyncRecord {
  id: string;
  runId: string;
  source: SyncSource;
  changeType: RecordChangeType;
  title: string;
  subtitle: string;
  arrivedAt: string;
  externalUrl: string;
  preview: PreviewPayload;
  diff?: DiffPayload;
  classification?: ClassificationPayload;
  summary?: SummaryPayload;
  errorMessage?: string;
}

export interface SyncRun {
  id: string;
  source: SyncSource;
  startedAt: string;
  durationMs: number;
  status: SyncStatus;
  counts: { new: number; updated: number; unchanged: number; failed: number };
  errorExcerpt?: string;
  records: SyncRecord[];
}

// Source metadata --------------------------------------------------
export interface SourceMeta {
  id: SyncSource;
  label: string;
  cadence: string;
}

export const SOURCES: SourceMeta[] = [
  { id: 'confluence', label: 'Confluence', cadence: 'every 4h' },
  { id: 'gmail', label: 'Gmail', cadence: 'every 5m' },
  { id: 'jira', label: 'Jira', cadence: 'hourly' },
  { id: 'gcal', label: 'Google Calendar', cadence: 'hourly' },
  { id: 'quickbooks', label: 'QuickBooks', cadence: 'daily' },
];

// Status + change-type visual maps --------------------------------
export const STATUS_DOT: Record<SyncStatus, string> = {
  success: 'bg-[#4a9a9a]',
  running: 'bg-[#e8a84c] animate-pulse',
  partial: 'bg-[#e8a84c]',
  failed: 'bg-[#f47067]',
};

export const STATUS_LABEL: Record<SyncStatus, string> = {
  success: 'Success',
  running: 'Running',
  partial: 'Partial',
  failed: 'Failed',
};

export const CHANGE_BADGE: Record<
  RecordChangeType,
  { label: string; classes: string }
> = {
  new: { label: 'NEW', classes: 'bg-[#4a9a9a]/20 text-[#4a9a9a]' },
  updated: { label: 'UPDATED', classes: 'bg-[#e8a84c]/20 text-[#e8a84c]' },
  failed: { label: 'FAILED', classes: 'bg-[#f47067]/20 text-[#f47067]' },
};

// =================================================================
// Mock dataset
// =================================================================
//
// 15 runs total (3 per source), 25 records across runs that had activity.
// Status distribution: 12 success, 1 running (most recent Gmail), 1 failed
// (Jira ~1d ago), 1 partial (Confluence ~2d ago with one FAILED record).
//
// Anchor: 2026-04-12T14:30:00Z. All timestamps strictly before that.

// ---------- Records ----------

const confluenceRecordsRunC1: SyncRecord[] = [
  {
    id: 'rec-conf-001',
    runId: 'run-conf-001',
    source: 'confluence',
    changeType: 'new',
    title: 'API Rate Limiting RFC',
    subtitle: 'Engineering / RFCs · by Sarah Chen',
    arrivedAt: '2026-04-12T12:31:14Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/ENG/pages/4815162342',
    preview: {
      source: 'confluence',
      markdown:
        '# API Rate Limiting RFC\n\n## Problem\n\nThe `/sync` endpoint is being hammered by aggressive client retries during cold-start windows, occasionally pushing Convex over its concurrent-mutation budget.\n\n## Proposal\n\nIntroduce a token-bucket limiter keyed on `(workspace_id, endpoint)` with a default of 60 req/min and a burst of 20.\n\n## Open questions\n\n- Should retries respect the `Retry-After` header verbatim, or apply a small jitter?\n- Where do we surface 429s in the agent UI?',
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.94,
      model: 'claude-haiku-4-5',
      latencyMs: 268,
      reasoning:
        'Document discusses API rate limiting design with technical proposal and open questions. Clear engineering RFC structure.',
    },
    summary: {
      chunkCount: 7,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'RFC proposing a token-bucket rate limiter on /sync keyed by workspace and endpoint, with open questions about retry behaviour and 429 surfacing in the agent UI.',
    },
  },
  {
    id: 'rec-conf-002',
    runId: 'run-conf-001',
    source: 'confluence',
    changeType: 'updated',
    title: 'Zenbot Architecture Overview',
    subtitle: 'Engineering / Architecture · edited by Matt Bourne',
    arrivedAt: '2026-04-12T12:31:18Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/ENG/pages/4815100018',
    preview: {
      source: 'confluence',
      markdown:
        '# Zenbot Architecture Overview\n\nZenbot is a personal assistant with three planes: voice, text chat, and async tasks.\n\n## Planes\n\n- **Voice** — LiveKit + Gemini realtime\n- **Text chat** — OpenRouter (Claude Haiku 4.5)\n- **Async tasks** — Termo + Codeman\n\nAll data flows through Convex.',
    },
    diff: {
      kind: 'prose',
      before:
        '# Zenbot Architecture Overview\n\nZenbot is a personal assistant with two planes: voice and text chat.\n\n## Planes\n\n- **Voice** — LiveKit + Gemini realtime\n- **Text chat** — OpenRouter (Claude Haiku 4.5)\n\nAll data flows through Convex.',
      after:
        '# Zenbot Architecture Overview\n\nZenbot is a personal assistant with three planes: voice, text chat, and async tasks.\n\n## Planes\n\n- **Voice** — LiveKit + Gemini realtime\n- **Text chat** — OpenRouter (Claude Haiku 4.5)\n- **Async tasks** — Termo + Codeman\n\nAll data flows through Convex.',
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.91,
      model: 'claude-haiku-4-5',
      latencyMs: 241,
      reasoning:
        'High-level system architecture document describing service planes and data flow. Engineering reference material.',
    },
    summary: {
      chunkCount: 5,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Top-level Zenbot architecture document, now describing three planes (voice, text chat, async tasks) and Convex as the shared data layer.',
    },
  },
  {
    id: 'rec-conf-003',
    runId: 'run-conf-001',
    source: 'confluence',
    changeType: 'new',
    title: 'Runbook: Certificate Rotation',
    subtitle: 'Operations / Runbooks · by Lisa Park',
    arrivedAt: '2026-04-12T12:31:23Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/OPS/pages/4815201101',
    preview: {
      source: 'confluence',
      markdown:
        '# Runbook: Certificate Rotation\n\n## When\n\nLetsEncrypt certs auto-renew 30 days before expiry. This runbook covers the manual fallback when the renew job fails.\n\n## Steps\n\n1. SSH to the Coolify host.\n2. Run `certbot renew --force-renewal -d <host>`.\n3. Reload Caddy via `coolify cli redeploy <app>`.\n4. Confirm cert in browser DevTools and post in #ops.',
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.88,
      model: 'claude-haiku-4-5',
      latencyMs: 202,
      reasoning:
        'Operational runbook with step-by-step recovery instructions for certificate renewal failures.',
    },
    summary: {
      chunkCount: 4,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Manual certificate rotation runbook for Coolify-hosted apps when the auto-renewal job fails: certbot force renewal then redeploy.',
    },
  },
  {
    id: 'rec-conf-006',
    runId: 'run-conf-001',
    source: 'confluence',
    changeType: 'new',
    title: 'Onboarding Checklist',
    subtitle: 'People Ops / Onboarding · by Lisa Park',
    arrivedAt: '2026-04-12T12:31:29Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/PEOPLE/pages/4815250012',
    preview: {
      source: 'confluence',
      markdown:
        '# Onboarding Checklist\n\n## Day 1\n\n- Issue laptop and 1Password vault invite\n- Set up Google Workspace + Slack\n- Walk through the Zenbot architecture deck\n\n## Week 1\n\n- Pair with the on-call engineer\n- Ship a one-line PR to break the seal\n- Book intro 1:1s with each team lead',
    },
    classification: {
      label: 'People Ops',
      confidence: 0.9,
      model: 'claude-haiku-4-5',
      latencyMs: 224,
      reasoning:
        'Standard new-hire onboarding checklist with day 1 and week 1 milestones. Operational people ops document.',
    },
    summary: {
      chunkCount: 4,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New-hire onboarding checklist covering day 1 setup (laptop, 1Password, Slack) and week 1 milestones (pairing with on-call, first PR, intro 1:1s).',
    },
  },
  {
    id: 'rec-conf-007',
    runId: 'run-conf-001',
    source: 'confluence',
    changeType: 'updated',
    title: 'Postmortem: April outage',
    subtitle: 'Engineering / Postmortems · edited by Sarah Chen',
    arrivedAt: '2026-04-12T12:31:34Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/ENG/pages/4815260077',
    preview: {
      source: 'confluence',
      markdown:
        '# Postmortem: April outage\n\n## Timeline\n\n- **08:14 UTC** — Convex backend OOM-killed by the host kernel\n- **08:17 UTC** — Coolify auto-restarted the container\n- **08:22 UTC** — Sync queue drained, service nominal\n\n## Root cause\n\nA runaway vector-search action held a 600 MB working set in memory. Container memory limit raised from 1 GB to 2 GB and the action now streams chunks instead of buffering.\n\n## Action items\n\n- [x] Raise memory limit\n- [x] Stream vector-search chunks\n- [ ] Add Convex memory alert in Sentry',
    },
    diff: {
      kind: 'prose',
      before:
        '# Postmortem: April outage\n\n## Timeline\n\n- **08:14 UTC** — Convex backend OOM-killed by the host kernel\n- **08:17 UTC** — Coolify auto-restarted the container\n- **08:22 UTC** — Sync queue drained, service nominal\n\n## Root cause\n\nA runaway vector-search action held a 600 MB working set in memory.\n\n## Action items\n\n- [ ] Raise memory limit\n- [ ] Stream vector-search chunks\n- [ ] Add Convex memory alert in Sentry',
      after:
        '# Postmortem: April outage\n\n## Timeline\n\n- **08:14 UTC** — Convex backend OOM-killed by the host kernel\n- **08:17 UTC** — Coolify auto-restarted the container\n- **08:22 UTC** — Sync queue drained, service nominal\n\n## Root cause\n\nA runaway vector-search action held a 600 MB working set in memory. Container memory limit raised from 1 GB to 2 GB and the action now streams chunks instead of buffering.\n\n## Action items\n\n- [x] Raise memory limit\n- [x] Stream vector-search chunks\n- [ ] Add Convex memory alert in Sentry',
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.93,
      model: 'claude-haiku-4-5',
      latencyMs: 247,
      reasoning:
        'Incident postmortem with timeline, root cause and action items. Standard engineering postmortem format.',
    },
    summary: {
      chunkCount: 6,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'April Convex outage postmortem: vector-search action OOM-killed the backend. Memory limit raised and chunks now streamed; Sentry alert still pending.',
    },
  },
];

const confluenceRecordsRunC2Partial: SyncRecord[] = [
  {
    id: 'rec-conf-004',
    runId: 'run-conf-002',
    source: 'confluence',
    changeType: 'failed',
    title: 'Q2 Budget Proposal',
    subtitle: 'Finance / Budgets · by Sarah Chen',
    arrivedAt: '2026-04-10T18:14:02Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/FIN/pages/4815300444',
    preview: {
      source: 'confluence',
      markdown: 'Content unavailable',
    },
    errorMessage: 'Markdown parse error at line 47',
  },
];

const gmailRecordsRunG2: SyncRecord[] = [
  {
    id: 'rec-gmail-001',
    runId: 'run-gmail-002',
    source: 'gmail',
    changeType: 'new',
    title: 'Q2 budget review — feedback?',
    subtitle: 'from Sarah Chen <sarah@contextloops.com>',
    arrivedAt: '2026-04-12T14:21:08Z',
    externalUrl: 'https://mail.google.com/mail/u/0/#inbox/189f4c7a1b2d3e4f',
    preview: {
      source: 'gmail',
      from: 'Sarah Chen <sarah@contextloops.com>',
      to: 'Matt Bourne <matt@contextloops.com>',
      subject: 'Q2 budget review — feedback?',
      body: 'Hey Matt,\n\nI pushed the Q2 budget proposal to Confluence this morning. Could you skim the infrastructure section before our 1:1 tomorrow? Specifically the line items for Hetzner and OpenAI — I think we can trim them once the Gemini migration lands.\n\nThanks,\nSarah',
    },
    classification: {
      label: 'Actionable',
      confidence: 0.93,
      model: 'claude-haiku-4-5',
      latencyMs: 211,
      reasoning:
        'Direct request for review with a soft deadline (1:1 tomorrow). Contains a specific ask and warrants a reply.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Sarah is asking Matt to review the infrastructure section of the Q2 budget proposal before tomorrow’s 1:1, especially Hetzner and OpenAI line items.',
    },
  },
  {
    id: 'rec-gmail-002',
    runId: 'run-gmail-002',
    source: 'gmail',
    changeType: 'new',
    title: 'Privacy Policy link request',
    subtitle: 'from Paul Reid <paul@indieapps.dev>',
    arrivedAt: '2026-04-12T14:22:41Z',
    externalUrl: 'https://mail.google.com/mail/u/0/#inbox/189f4c7a1b2d3e50',
    preview: {
      source: 'gmail',
      from: 'Paul Reid <paul@indieapps.dev>',
      to: 'Matt Bourne <matt@contextloops.com>',
      subject: 'Privacy Policy link request',
      body: 'Hi Matt — quick one. Apple just bounced our app review again and they want a working link to a privacy policy that matches the Zenbot data flows we discussed. Do you have a hosted URL I can drop into App Store Connect?\n\nCheers,\nPaul',
    },
    classification: {
      label: 'Actionable',
      confidence: 0.96,
      model: 'claude-haiku-4-5',
      latencyMs: 198,
      reasoning:
        'External party blocked on a specific deliverable (privacy policy URL). Apple review timeline implies urgency.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Paul Reid needs a hosted privacy policy URL to attach to App Store Connect for an Apple review resubmission.',
    },
  },
  {
    id: 'rec-gmail-003',
    runId: 'run-gmail-002',
    source: 'gmail',
    changeType: 'new',
    title: 'SSL cert rotation completed',
    subtitle: 'from Lisa Park <lisa@contextloops.com>',
    arrivedAt: '2026-04-12T14:24:55Z',
    externalUrl: 'https://mail.google.com/mail/u/0/#inbox/189f4c7a1b2d3e51',
    preview: {
      source: 'gmail',
      from: 'Lisa Park <lisa@contextloops.com>',
      to: 'eng@contextloops.com',
      subject: 'SSL cert rotation completed',
      body: 'Heads up — I rotated the SSL certs on zenbot-nango and the Coolify proxy this morning. Both apps redeployed cleanly. New expiry is 2026-07-11. No action required.\n\nLisa',
    },
    classification: {
      label: 'Information',
      confidence: 0.89,
      model: 'claude-haiku-4-5',
      latencyMs: 184,
      reasoning:
        'FYI status update. Explicitly states no action required and provides closing context (new expiry date).',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Lisa rotated the SSL certs on zenbot-nango and the Coolify proxy. New expiry is 2026-07-11, no action required.',
    },
  },
  {
    id: 'rec-gmail-005',
    runId: 'run-gmail-002',
    source: 'gmail',
    changeType: 'new',
    title: 'Invoice reminder — Hetzner',
    subtitle: 'from billing@hetzner.com',
    arrivedAt: '2026-04-12T14:26:11Z',
    externalUrl: 'https://mail.google.com/mail/u/0/#inbox/189f4c7a1b2d3e52',
    preview: {
      source: 'gmail',
      from: 'Hetzner Billing <billing@hetzner.com>',
      to: 'accounts@contextloops.com',
      subject: 'Invoice reminder — Hetzner',
      body: 'Dear customer,\n\nThis is a friendly reminder that invoice INV-2026-041 (€248.00) is due on 2026-04-25. Please ensure payment is sent to the bank details on file.\n\nKind regards,\nHetzner Billing',
    },
    classification: {
      label: 'Information',
      confidence: 0.86,
      model: 'claude-haiku-4-5',
      latencyMs: 192,
      reasoning:
        'Automated billing reminder for an invoice already in QuickBooks. Informational, no immediate action required.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Automated Hetzner reminder that invoice INV-2026-041 for €248 is due on 2026-04-25.',
    },
  },
];

const gmailRecordsRunG3: SyncRecord[] = [
  {
    id: 'rec-gmail-004',
    runId: 'run-gmail-003',
    source: 'gmail',
    changeType: 'new',
    title: 'Acme partnership draft',
    subtitle: 'from James Wright <james@acme.io>',
    arrivedAt: '2026-04-12T13:48:12Z',
    externalUrl: 'https://mail.google.com/mail/u/0/#inbox/189f4b1c8e0d2a99',
    preview: {
      source: 'gmail',
      from: 'James Wright <james@acme.io>',
      to: 'Matt Bourne <matt@contextloops.com>',
      subject: 'Acme partnership draft',
      body: 'Matt,\n\nAttaching a first draft of the partnership memo. The commercial terms section is still TBD pending our Friday call. Curious whether you’d prefer revenue share or a flat per-seat model — happy to model both.\n\nJames',
    },
    classification: {
      label: 'Actionable',
      confidence: 0.87,
      model: 'claude-haiku-4-5',
      latencyMs: 233,
      reasoning:
        'Partner is requesting a decision on commercial terms ahead of a Friday call. Requires Matt to weigh in.',
    },
    summary: {
      chunkCount: 4,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'James shared a draft Acme partnership memo and is asking Matt to choose between a revenue-share or flat per-seat commercial model before Friday’s call.',
    },
  },
  {
    id: 'rec-gmail-006',
    runId: 'run-gmail-003',
    source: 'gmail',
    changeType: 'new',
    title: 'Design review follow-up',
    subtitle: 'from Sarah Chen <sarah@contextloops.com>',
    arrivedAt: '2026-04-12T13:49:47Z',
    externalUrl: 'https://mail.google.com/mail/u/0/#inbox/189f4b1c8e0d2a9a',
    preview: {
      source: 'gmail',
      from: 'Sarah Chen <sarah@contextloops.com>',
      to: 'Matt Bourne <matt@contextloops.com>',
      subject: 'Design review follow-up',
      body: 'Matt — sending you the action items from yesterday’s design review. Three small Tailwind tweaks on the sync history page and one open question about whether we expose the diff viewer for QuickBooks invoices. Can you sanity-check the Figma frame and reply by EOD?\n\nSarah',
    },
    classification: {
      label: 'Actionable',
      confidence: 0.91,
      model: 'claude-haiku-4-5',
      latencyMs: 205,
      reasoning:
        'Action items from a design review with an explicit EOD reply request. Requires Matt to review the Figma and respond.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Sarah sent action items from the design review: three Tailwind tweaks on the sync history page and an open question about exposing the diff viewer for QuickBooks invoices. EOD reply requested.',
    },
  },
];

const jiraRecordsRunJ1: SyncRecord[] = [
  {
    id: 'rec-jira-001',
    runId: 'run-jira-001',
    source: 'jira',
    changeType: 'updated',
    title: 'ZEN-142 Rate limiting for /sync endpoint',
    subtitle: 'Zenbot · Story · updated by Sarah Chen',
    arrivedAt: '2026-04-12T13:01:44Z',
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-142',
    preview: {
      source: 'jira',
      fields: [
        { label: 'Status', value: 'In Review' },
        { label: 'Assignee', value: 'Sarah Chen' },
        { label: 'Priority', value: 'High' },
        { label: 'Reporter', value: 'Matt Bourne' },
      ],
      description:
        'Implement a token-bucket rate limiter on the /sync endpoint keyed on (workspace_id, endpoint). Default 60 req/min with a burst of 20. Surface 429s with a Retry-After header and reflect them in the agent UI as a transient banner.',
    },
    diff: {
      kind: 'fields',
      changes: [
        { label: 'Status', before: 'In Progress', after: 'In Review' },
        { label: 'Assignee', before: 'Unassigned', after: 'Sarah Chen' },
      ],
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.85,
      model: 'claude-haiku-4-5',
      latencyMs: 219,
      reasoning:
        'Engineering ticket with technical implementation details for rate limiting. Belongs to engineering tracking.',
    },
    summary: {
      chunkCount: 4,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Story to add a token-bucket rate limiter to /sync. Just moved into review with Sarah Chen as assignee.',
    },
  },
  {
    id: 'rec-jira-002',
    runId: 'run-jira-001',
    source: 'jira',
    changeType: 'updated',
    title: 'ZEN-138 SSL cert renewal automation',
    subtitle: 'Zenbot · Task · updated by Lisa Park',
    arrivedAt: '2026-04-12T13:01:52Z',
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-138',
    preview: {
      source: 'jira',
      fields: [
        { label: 'Status', value: 'Done' },
        { label: 'Assignee', value: 'Lisa Park' },
        { label: 'Priority', value: 'Medium' },
        { label: 'Reporter', value: 'Matt Bourne' },
      ],
      description:
        'Wire the certbot renewal hook into the Coolify post-deploy job so renewals don’t require a manual SSH session. Closed out after the 2026-04-12 rotation completed cleanly.',
    },
    diff: {
      kind: 'fields',
      changes: [
        { label: 'Status', before: 'In Review', after: 'Done' },
        { label: 'Resolution', before: '—', after: 'Fixed' },
      ],
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.82,
      model: 'claude-haiku-4-5',
      latencyMs: 207,
      reasoning:
        'Operations automation task that just closed out. Belongs to engineering history.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Cert renewal automation task closed as Fixed after the 2026-04-12 rotation completed cleanly via the Coolify post-deploy hook.',
    },
  },
  {
    id: 'rec-jira-004',
    runId: 'run-jira-001',
    source: 'jira',
    changeType: 'new',
    title: 'ZEN-145 Vector search index rotation',
    subtitle: 'Zenbot · Task · created by Matt Bourne',
    arrivedAt: '2026-04-12T13:02:01Z',
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-145',
    preview: {
      source: 'jira',
      fields: [
        { label: 'Status', value: 'To Do' },
        { label: 'Assignee', value: 'Sarah Chen' },
        { label: 'Priority', value: 'Medium' },
        { label: 'Reporter', value: 'Matt Bourne' },
      ],
      description:
        'Add a scheduled job that rotates the Convex vector index every 30 days to keep query latency consistent. Should run during the maintenance window and post a summary in #ops.',
    },
    classification: {
      label: 'Engineering Docs',
      confidence: 0.87,
      model: 'claude-haiku-4-5',
      latencyMs: 213,
      reasoning:
        'Engineering task to schedule periodic vector index rotation in Convex. Operational engineering work.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New ZEN-145 task to rotate the Convex vector index every 30 days during the maintenance window, with a summary posted to #ops.',
    },
  },
];

const jiraRecordsRunJ3TMA: SyncRecord[] = [
  {
    id: 'rec-jira-003',
    runId: 'run-jira-003',
    source: 'jira',
    changeType: 'new',
    title: 'TMA-89 Patient onboarding redesign',
    subtitle: 'TMA · Story · created by James Wright',
    arrivedAt: '2026-04-09T11:14:30Z',
    externalUrl: 'https://contextloops.atlassian.net/browse/TMA-89',
    preview: {
      source: 'jira',
      fields: [
        { label: 'Status', value: 'To Do' },
        { label: 'Assignee', value: 'Unassigned' },
        { label: 'Priority', value: 'Medium' },
        { label: 'Reporter', value: 'James Wright' },
      ],
      description:
        'Redesign the patient onboarding flow to collapse the four-step intake into a single scrollable form with inline validation. Acceptance criteria pending product sign-off.',
    },
    classification: {
      label: 'Product',
      confidence: 0.84,
      model: 'claude-haiku-4-5',
      latencyMs: 256,
      reasoning:
        'Product story focused on user-facing redesign of an onboarding flow. Belongs to product tracking.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New TMA story to collapse the four-step patient intake into a single scrollable form with inline validation.',
    },
  },
];

const gcalRecordsRunCal1: SyncRecord[] = [
  {
    id: 'rec-gcal-001',
    runId: 'run-gcal-001',
    source: 'gcal',
    changeType: 'updated',
    title: 'Q2 budget review',
    subtitle: 'Tomorrow · 1 attendee changed time',
    arrivedAt: '2026-04-12T13:45:09Z',
    externalUrl: 'https://calendar.google.com/calendar/u/0/r/eventedit/q2budgetreview',
    preview: {
      source: 'gcal',
      start: '2026-04-13T15:00:00Z',
      end: '2026-04-13T15:45:00Z',
      attendees: ['matt@contextloops.com', 'sarah@contextloops.com'],
      location: 'Google Meet',
      description:
        'Walk through the Q2 budget proposal — focus on infrastructure line items (Hetzner, OpenAI) ahead of finance sign-off.',
    },
    diff: {
      kind: 'fields',
      changes: [
        {
          label: 'Time',
          before: 'Mon 13 Apr 2026, 14:00–14:30 UTC',
          after: 'Mon 13 Apr 2026, 15:00–15:45 UTC',
        },
      ],
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        '45-minute meeting to walk Sarah and Matt through Q2 budget variance and agree Q3 adjustments. Now rescheduled to 15:00 UTC.',
    },
  },
  {
    id: 'rec-gcal-002',
    runId: 'run-gcal-001',
    source: 'gcal',
    changeType: 'new',
    title: 'Acme partnership call',
    subtitle: 'Friday · 3 attendees',
    arrivedAt: '2026-04-12T13:45:14Z',
    externalUrl: 'https://calendar.google.com/calendar/u/0/r/eventedit/acmepartnership',
    preview: {
      source: 'gcal',
      start: '2026-04-17T16:00:00Z',
      end: '2026-04-17T17:00:00Z',
      attendees: [
        'matt@contextloops.com',
        'james@acme.io',
        'sarah@contextloops.com',
      ],
      location: 'Google Meet',
      description:
        'Walk through the Acme partnership memo and pick a commercial model (revenue share vs flat per-seat).',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New 1-hour Acme partnership call with Matt, James and Sarah on Friday to walk through the partnership memo and pick a commercial model.',
    },
  },
];

const gcalRecordsRunCal2: SyncRecord[] = [
  {
    id: 'rec-gcal-003',
    runId: 'run-gcal-002',
    source: 'gcal',
    changeType: 'new',
    title: '1:1 with Sarah',
    subtitle: 'Tomorrow · 2 attendees',
    arrivedAt: '2026-04-11T22:45:08Z',
    externalUrl: 'https://calendar.google.com/calendar/u/0/r/eventedit/oneonone-sarah',
    preview: {
      source: 'gcal',
      start: '2026-04-13T09:30:00Z',
      end: '2026-04-13T10:00:00Z',
      attendees: ['matt@contextloops.com', 'sarah@contextloops.com'],
      location: 'Google Meet',
      description:
        'Weekly 1:1 with Sarah. Standing agenda — sprint progress, blockers, growth chat.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New weekly 1:1 with Sarah on Monday morning, 30 minutes on Google Meet covering sprint progress, blockers and growth.',
    },
  },
];

const gcalRecordsRunCal3: SyncRecord[] = [
  {
    id: 'rec-gcal-004',
    runId: 'run-gcal-003',
    source: 'gcal',
    changeType: 'new',
    title: 'Engineering standup',
    subtitle: 'Daily · 4 attendees',
    arrivedAt: '2026-04-10T07:45:11Z',
    externalUrl: 'https://calendar.google.com/calendar/u/0/r/eventedit/eng-standup',
    preview: {
      source: 'gcal',
      start: '2026-04-10T08:30:00Z',
      end: '2026-04-10T08:45:00Z',
      attendees: [
        'matt@contextloops.com',
        'sarah@contextloops.com',
        'lisa@contextloops.com',
        'james@acme.io',
      ],
      location: 'Google Meet',
      description:
        'Daily 15-minute engineering standup. Round-robin updates and blockers, focus on the sync history launch.',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Daily 15-minute engineering standup on Google Meet. Round-robin updates with the team focused on shipping the sync history page.',
    },
  },
];

const quickbooksRecordsRunQ1: SyncRecord[] = [
  {
    id: 'rec-qb-001',
    runId: 'run-qb-001',
    source: 'quickbooks',
    changeType: 'new',
    title: 'INV-2026-041 Hetzner Hosting',
    subtitle: 'New invoice · €248.00',
    arrivedAt: '2026-04-12T08:02:18Z',
    externalUrl: 'https://qbo.intuit.com/app/invoice?txnId=2026-041',
    preview: {
      source: 'quickbooks',
      number: 'INV-2026-041',
      customer: 'Context Loops Ltd',
      lineItems: [
        { label: 'Hetzner Cloud — Containers', amount: '€158.00' },
        { label: 'Hetzner Cloud — Containers2', amount: '€72.00' },
        { label: 'Backups & snapshots', amount: '€18.00' },
      ],
      total: '€248.00',
      status: 'Awaiting payment',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New Hetzner hosting invoice INV-2026-041 totalling €248 covering Containers, Containers2, and backups for the April billing period.',
    },
  },
  {
    id: 'rec-qb-002',
    runId: 'run-qb-001',
    source: 'quickbooks',
    changeType: 'new',
    title: 'INV-2026-039 OpenAI API',
    subtitle: 'New invoice · $412.66',
    arrivedAt: '2026-04-12T08:02:25Z',
    externalUrl: 'https://qbo.intuit.com/app/invoice?txnId=2026-039',
    preview: {
      source: 'quickbooks',
      number: 'INV-2026-039',
      customer: 'Context Loops Ltd',
      lineItems: [
        { label: 'GPT-4o-mini realtime', amount: '$298.40' },
        { label: 'text-embedding-3-small', amount: '$84.10' },
        { label: 'Whisper transcription', amount: '$30.16' },
      ],
      total: '$412.66',
      status: 'Paid',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'OpenAI API invoice INV-2026-039 for $412.66 covering realtime voice, embeddings, and Whisper transcription. Marked as paid.',
    },
  },
];

const quickbooksRecordsRunQ2: SyncRecord[] = [
  {
    id: 'rec-qb-003',
    runId: 'run-qb-002',
    source: 'quickbooks',
    changeType: 'new',
    title: 'INV-2026-042 Hetzner Object Storage',
    subtitle: 'New invoice · €38.40',
    arrivedAt: '2026-04-11T08:02:14Z',
    externalUrl: 'https://qbo.intuit.com/app/invoice?txnId=2026-042',
    preview: {
      source: 'quickbooks',
      number: 'INV-2026-042',
      customer: 'Context Loops Ltd',
      lineItems: [
        { label: 'Object Storage — eu-central', amount: '€32.00' },
        { label: 'Egress traffic', amount: '€6.40' },
      ],
      total: '€38.40',
      status: 'Awaiting payment',
    },
    summary: {
      chunkCount: 3,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'New Hetzner Object Storage invoice INV-2026-042 for €38.40 covering eu-central storage and egress traffic for April.',
    },
  },
];

const quickbooksRecordsRunQ3: SyncRecord[] = [
  {
    id: 'rec-qb-004',
    runId: 'run-qb-003',
    source: 'quickbooks',
    changeType: 'new',
    title: 'INV-2026-043 Anthropic API',
    subtitle: 'New invoice · $186.20',
    arrivedAt: '2026-04-06T08:02:12Z',
    externalUrl: 'https://qbo.intuit.com/app/invoice?txnId=2026-043',
    preview: {
      source: 'quickbooks',
      number: 'INV-2026-043',
      customer: 'Context Loops Ltd',
      lineItems: [
        { label: 'Claude Haiku 4.5 — text chat', amount: '$112.40' },
        { label: 'Claude Haiku 4.5 — sender classifier', amount: '$48.60' },
        { label: 'Prompt caching read', amount: '$25.20' },
      ],
      total: '$186.20',
      status: 'Paid',
    },
    summary: {
      chunkCount: 4,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'Anthropic API invoice INV-2026-043 for $186.20 covering Claude Haiku usage on text chat, the sender classifier, and prompt cache reads. Marked as paid.',
    },
  },
];

const confluenceRecordsRunC3: SyncRecord[] = [
  {
    id: 'rec-conf-005',
    runId: 'run-conf-003',
    source: 'confluence',
    changeType: 'updated',
    title: 'Product Roadmap H1 2026',
    subtitle: 'Product / Roadmaps · edited by Matt Bourne',
    arrivedAt: '2026-04-08T09:18:44Z',
    externalUrl: 'https://contextloops.atlassian.net/wiki/spaces/PROD/pages/4815400990',
    preview: {
      source: 'confluence',
      markdown:
        '# Product Roadmap H1 2026\n\n## Themes\n\n- Voice-first capture\n- Async workflows with approval gates\n- Email triage\n\n## Q2 commitments\n\n- Termo skill substrate GA\n- Codeman session substrate beta\n- Sync history page',
    },
    diff: {
      kind: 'prose',
      before:
        '# Product Roadmap H1 2026\n\n## Themes\n\n- Voice-first capture\n- Async workflows with approval gates\n- Email triage\n\n## Q2 commitments\n\n- Termo skill substrate GA\n- Codeman session substrate beta',
      after:
        '# Product Roadmap H1 2026\n\n## Themes\n\n- Voice-first capture\n- Async workflows with approval gates\n- Email triage\n\n## Q2 commitments\n\n- Termo skill substrate GA\n- Codeman session substrate beta\n- Sync history page',
    },
    classification: {
      label: 'Product',
      confidence: 0.92,
      model: 'claude-haiku-4-5',
      latencyMs: 251,
      reasoning:
        'High-level product roadmap document listing themes and Q2 commitments. Clear product planning artifact.',
    },
    summary: {
      chunkCount: 6,
      embeddingModel: 'text-embedding-3-small',
      summary:
        'H1 2026 product roadmap covering voice-first capture, async workflows, and email triage. Sync history page just added to the Q2 commitments list.',
    },
  },
];

// ---------- Runs ----------
//
// Newest first. 15 entries total.
//
// Status distribution check:
//   - 12 success
//   - 1 running   (run-gmail-001 — most recent Gmail)
//   - 1 failed    (run-jira-002 — ~1 day ago)
//   - 1 partial   (run-conf-002 — ~2 days ago, 1 failed record)

export const SYNC_RUNS: SyncRun[] = [
  // Gmail — running (most recent)
  {
    id: 'run-gmail-001',
    source: 'gmail',
    startedAt: '2026-04-12T14:29:10Z',
    durationMs: 0,
    status: 'running',
    counts: { new: 0, updated: 0, unchanged: 0, failed: 0 },
    records: [],
  },
  // Gmail — most recent completed (4 new emails)
  {
    id: 'run-gmail-002',
    source: 'gmail',
    startedAt: '2026-04-12T14:21:00Z',
    durationMs: 4180,
    status: 'success',
    counts: { new: 4, updated: 0, unchanged: 142, failed: 0 },
    records: gmailRecordsRunG2,
  },
  // Gmail — earlier today, 2 new emails
  {
    id: 'run-gmail-003',
    source: 'gmail',
    startedAt: '2026-04-12T13:48:00Z',
    durationMs: 3960,
    status: 'success',
    counts: { new: 2, updated: 0, unchanged: 138, failed: 0 },
    records: gmailRecordsRunG3,
  },
  // GCal — Q2 budget review time change + new Acme call
  {
    id: 'run-gcal-001',
    source: 'gcal',
    startedAt: '2026-04-12T13:45:00Z',
    durationMs: 1820,
    status: 'success',
    counts: { new: 1, updated: 1, unchanged: 47, failed: 0 },
    records: gcalRecordsRunCal1,
  },
  // Jira — ZEN-142 + ZEN-138 updates + ZEN-145 new
  {
    id: 'run-jira-001',
    source: 'jira',
    startedAt: '2026-04-12T13:01:30Z',
    durationMs: 5210,
    status: 'success',
    counts: { new: 1, updated: 2, unchanged: 84, failed: 0 },
    records: jiraRecordsRunJ1,
  },
  // Confluence — most recent successful run, 5 records
  {
    id: 'run-conf-001',
    source: 'confluence',
    startedAt: '2026-04-12T12:31:00Z',
    durationMs: 18420,
    status: 'success',
    counts: { new: 3, updated: 2, unchanged: 1893, failed: 0 },
    records: confluenceRecordsRunC1,
  },
  // QuickBooks — daily, 2 new invoices
  {
    id: 'run-qb-001',
    source: 'quickbooks',
    startedAt: '2026-04-12T08:02:00Z',
    durationMs: 6240,
    status: 'success',
    counts: { new: 2, updated: 0, unchanged: 312, failed: 0 },
    records: quickbooksRecordsRunQ1,
  },
  // GCal — earlier success with new 1:1
  {
    id: 'run-gcal-002',
    source: 'gcal',
    startedAt: '2026-04-11T22:45:00Z',
    durationMs: 1640,
    status: 'success',
    counts: { new: 1, updated: 0, unchanged: 47, failed: 0 },
    records: gcalRecordsRunCal2,
  },
  // Jira — failed run ~1 day ago
  {
    id: 'run-jira-002',
    source: 'jira',
    startedAt: '2026-04-11T13:01:00Z',
    durationMs: 2110,
    status: 'failed',
    counts: { new: 0, updated: 0, unchanged: 0, failed: 1 },
    errorExcerpt:
      'AtlassianAPIError: 401 Unauthorized — token rejected on /rest/api/3/search (request id 7c1a8f). Refresh credentials in Nango.',
    records: [],
  },
  // QuickBooks — 1 new invoice (Object Storage)
  {
    id: 'run-qb-002',
    source: 'quickbooks',
    startedAt: '2026-04-11T08:02:00Z',
    durationMs: 5870,
    status: 'success',
    counts: { new: 1, updated: 0, unchanged: 312, failed: 0 },
    records: quickbooksRecordsRunQ2,
  },
  // Confluence — partial run ~2 days ago (1 failed record)
  {
    id: 'run-conf-002',
    source: 'confluence',
    startedAt: '2026-04-10T18:13:00Z',
    durationMs: 16240,
    status: 'partial',
    counts: { new: 0, updated: 0, unchanged: 1892, failed: 1 },
    errorExcerpt:
      'ConfluenceParseError: Markdown parse error at line 47 while processing "Q2 Budget Proposal". 1 of 1893 documents skipped.',
    records: confluenceRecordsRunC2Partial,
  },
  // GCal — older success with new standup
  {
    id: 'run-gcal-003',
    source: 'gcal',
    startedAt: '2026-04-10T07:45:00Z',
    durationMs: 1750,
    status: 'success',
    counts: { new: 1, updated: 0, unchanged: 46, failed: 0 },
    records: gcalRecordsRunCal3,
  },
  // Jira — TMA-89 new story ~3 days ago
  {
    id: 'run-jira-003',
    source: 'jira',
    startedAt: '2026-04-09T11:14:00Z',
    durationMs: 4880,
    status: 'success',
    counts: { new: 1, updated: 0, unchanged: 83, failed: 0 },
    records: jiraRecordsRunJ3TMA,
  },
  // Confluence — older success, Product Roadmap edit
  {
    id: 'run-conf-003',
    source: 'confluence',
    startedAt: '2026-04-08T09:18:00Z',
    durationMs: 17910,
    status: 'success',
    counts: { new: 0, updated: 1, unchanged: 1891, failed: 0 },
    records: confluenceRecordsRunC3,
  },
  // QuickBooks — oldest run within window, Anthropic invoice
  {
    id: 'run-qb-003',
    source: 'quickbooks',
    startedAt: '2026-04-06T08:02:00Z',
    durationMs: 5710,
    status: 'success',
    counts: { new: 1, updated: 0, unchanged: 310, failed: 0 },
    records: quickbooksRecordsRunQ3,
  },
];

// ---------- Helpers ----------

export function runsBySource(): Record<SyncSource, SyncRun[]> {
  const out = Object.fromEntries(
    SOURCES.map((s) => [s.id, [] as SyncRun[]]),
  ) as Record<SyncSource, SyncRun[]>;
  for (const run of SYNC_RUNS) out[run.source].push(run);
  for (const id of Object.keys(out) as SyncSource[]) {
    out[id].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }
  return out;
}

export function relativeTime(
  iso: string,
  nowIso = '2026-04-12T14:30:00Z',
): string {
  const diff = new Date(nowIso).getTime() - new Date(iso).getTime();
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function formatDuration(ms: number): string {
  if (ms === 0) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
